import qs from 'qs'
import { isValid, parseISO } from 'date-fns'
import { isEmpty, isEqual } from 'lodash'
import {
  customDateRangeLabel,
  customOption,
  DEFAULT_DATE_RANGE_OPTION,
  getDateRangeFromOption,
  getDateRangeOptions,
} from 'components/organisms/dashboard/dateRangePicker/helpers'

import type { DateRangeOption } from 'state/dashboard/types'

// What actually lives in client settings: a relative selection persists as `{ key }` only,
// while a custom range persists its full window. Values are read back from untyped JSON, so
// every field except the key must be treated as potentially absent.
export type StorableDateRange = Pick<DateRangeOption, 'key'> & Partial<DateRangeOption>

type CompleteCustomRange = StorableDateRange & {
  key: 'custom'
  value: DateRangeOption['value']
}

export const DEFAULT_DATE_RANGE = getDateRangeFromOption(DEFAULT_DATE_RANGE_OPTION)

export const buildDateRangeSettingKey = (category: string) => `${category}.dateRange`

const isCompleteCustomRange = (dateRange: StorableDateRange): dateRange is CompleteCustomRange => {
  const { startDate, endDate } = dateRange.value ?? {}
  if (dateRange.key !== 'custom' || typeof startDate !== 'string' || typeof endDate !== 'string')
    return false

  const parsedStartDate = parseISO(startDate)
  const parsedEndDate = parseISO(endDate)
  return (
    isValid(parsedStartDate) &&
    isValid(parsedEndDate) &&
    parsedStartDate.getTime() <= parsedEndDate.getTime()
  )
}

const resolveDateRange = (dateRange: StorableDateRange): DateRangeOption => {
  if (isCompleteCustomRange(dateRange)) {
    return {
      key: 'custom',
      label: customDateRangeLabel(dateRange.value),
      value: dateRange.value,
    }
  }
  const freshOption =
    getDateRangeOptions().find((option) => option.key === dateRange.key && option.key !== 'custom') ??
    DEFAULT_DATE_RANGE_OPTION
  return getDateRangeFromOption(freshOption)
}

export const toStorableDateRange = (dateRange: DateRangeOption): StorableDateRange => {
  if (dateRange.key === 'custom') return { key: dateRange.key, value: dateRange.value }
  return { key: dateRange.key }
}

export const getEffectiveDateRange = ({
  urlDateRange,
  fetchedDateRange,
}: {
  urlDateRange?: DateRangeOption
  fetchedDateRange?: StorableDateRange
}): DateRangeOption => {
  const savedDateRange = resolveDateRange(
    fetchedDateRange && !isEmpty(fetchedDateRange) ? fetchedDateRange : DEFAULT_DATE_RANGE
  )

  if (!urlDateRange || isEmpty(urlDateRange)) {
    return savedDateRange
  }

  return isEqual(urlDateRange.value, savedDateRange.value) ? savedDateRange : urlDateRange
}

export const getDateRangeFromUrl = (): DateRangeOption | undefined => {
  const { filter = {} } = qs.parse(window.location.search.substring(1)) as { filter?: { start_date?: string; end_date?: string } }
  const { start_date, end_date } = filter
  if (start_date && end_date)
    return getDateRangeFromOption(customOption, { startDate: start_date, endDate: end_date })
}
